import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { FormLockedType } from '../../../types/FormLockedType'
import {
  FlyoutProductSections,
  ContractDimension,
} from '../../../types/addEditProduct'
import {
  BoxV2,
  ButtonV2,
  LabraToolTipV2,
  NoteV2,
  TextInputV2,
} from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { Grid, Typography } from '@mui/material'
import { useStyles } from './UsageDimensions.styles'
import { useEffect, useMemo } from 'react'
import {
  getRandom,
  getUnique12Number,
} from '../../../common/utils/helperFunctions'
import { get, isEmpty } from 'lodash'
import {
  dimensionNameRegex,
  giveNumberInputValue,
} from '../../../common/utils/productCreationUtil'
import { AwsDimensionCategories } from '../../AddEditProduct/AddEditProductListing/FlyoutProductDimensionsSection/FlyoutProductDimensionsSection'
import clsx from 'clsx'
import { getListingTextHighlights } from '../../../common/utils/listingTextRestrictions'
import { useFlag } from '@labrav/flags'

interface UsageDimensionsProps {
  requirementsLink: string
  isSaasSubscription: boolean
  awsDimensionCategories: AwsDimensionCategories
  detailView?: boolean
  detailViewTooltip?: string
  productView?: boolean
}

const emptyAdditionalDimensionObj = {
  name: '',
  label: '',
  price: null,
  price1m: null,
  price12m: null,
  price24m: null,
  price36m: null,
  additionalUsagePrice: null,
  additionalUsageDescription: null,
  subscriptionPrice: null,
  labelDescription: null,
  isAdditionalUsageAllowed: true,
  checkBoxAdditionalUsageAllowed: false,
}
export const UsageDimensions = ({
  isFormLocked,
  isSaasSubscription,
  awsDimensionCategories,
  detailView,
  detailViewTooltip,
  productView,
}: UsageDimensionsProps & FormLockedType) => {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const { classes } = useStyles()
  const { flag: listingValidation } = useFlag('listingValidation')
  const additionalUsageDimentions =
    values.productDimensions.additionalDimensions || []
  const dimensionLength = additionalUsageDimentions.length
  const dimensionCategoryName = values.productDimensions.dimensionCategoryName
  const { usageMap } = awsDimensionCategories
  const usageBasedUnitType =
    Object.keys(usageMap).length > 0 ? usageMap[dimensionCategoryName] : ''

  const dimensionCategoryType = isSaasSubscription
    ? dimensionCategoryName
    : usageBasedUnitType

  const emptyDimensionObj = {
    ...emptyAdditionalDimensionObj,
    isAdditionalUsageAllowed: isSaasSubscription ? false : true,
    dimensionUnit: dimensionCategoryType,
  }
  useEffect(() => {
    const defaultFieldForSubscription =
      isSaasSubscription &&
      isEmpty(values?.productDimensions?.additionalDimensions)
    if (defaultFieldForSubscription) {
      setFieldValue('productDimensions.additionalDimensions', [
        emptyDimensionObj,
      ])
    }
  }, [isSaasSubscription, additionalUsageDimentions])

  useEffect(() => {
    if (dimensionCategoryName) {
      setFieldValue(
        'productDimensions.additionalDimensions',
        additionalUsageDimentions.map(dimension => ({
          ...dimension,
          dimensionUnit: dimensionCategoryType,
        }))
      )
    }
  }, [dimensionCategoryName, usageMap])

  const dimensionsTouched = touched.productDimensions
  const productDimensions = values.productDimensions

  const productDimensionsErrors = errors.productDimensions
  const contractDimensions = productDimensions.dimensions
  const randomKey = useMemo(() => getRandom(), [dimensionLength])

  const getIsError = (path: string) => {
    return !!(
      get(dimensionsTouched, path, false) &&
      get(productDimensionsErrors, path, false)
    )
  }
  const getErrorText = (path: string) => {
    return (
      (!!get(dimensionsTouched, path, false) &&
        get(productDimensionsErrors, path, '')) ||
      ''
    )
  }
  const changeIsAdditionalUsageAllowedOnContractDimension = async (
    name: string
  ) => {
    const newProductDimension = contractDimensions.map(
      (obj: ContractDimension) => {
        if (obj.name === name) {
          obj.checkBoxAdditionalUsageAllowed = false
        }
        return obj
      }
    )

    await setFieldValue('productDimensions.dimensions', newProductDimension)
  }
  const getInputKey = (
    isFromContractDimension: boolean,
    key: 'price' | 'label' | 'descrip'
  ): string => {
    switch (key) {
      case 'descrip':
        return isFromContractDimension
          ? 'additionalUsageDescription'
          : 'labelDescription'
      case 'label':
        return 'label'
      default:
        return isFromContractDimension
          ? 'additionalUsagePrice'
          : 'subscriptionPrice'
    }
  }
  const handleChangePrice = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    dimension: ContractDimension
  ) => {
    await setFieldValue(
      `productDimensions.additionalDimensions.${index}.${getInputKey(
        dimension.isAdditionalUsageAllowed || false,
        'price'
      )}`,
      e.target.value === '' ? null : e.target.value
    )
  }
  const handleChangeLabel = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    dimension: ContractDimension
  ) => {
    await setFieldValue(
      `productDimensions.additionalDimensions.${index}.${getInputKey(
        dimension.isAdditionalUsageAllowed || false,
        'label'
      )}`,
      e.target.value === '' ? null : e.target.value
    )
  }

  const handleDescriptionChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    dimension: ContractDimension
  ) => {
    const value = e.target.value as string
    const keyName = getInputKey(
      dimension.isAdditionalUsageAllowed || false,
      'descrip'
    )

    const updatedDimensions = [...additionalUsageDimentions]
    const isExisting = updatedDimensions[index].isExisting
    const uniquleName = `${value
      .split(' ')
      .slice(0, 3)
      .join()
      .substring(0, 20)
      .replace(dimensionNameRegex, '_')}_${getUnique12Number()}`
    updatedDimensions[index] = {
      ...updatedDimensions[index],
      [keyName]: value,
      ...(!dimension.checkBoxAdditionalUsageAllowed
        ? {
            ...(!isExisting ? { name: uniquleName } : {}),
          }
        : {}),
    }
    await setFieldValue(
      'productDimensions.additionalDimensions',
      updatedDimensions
    )
  }

  const isAdditionalDimensionEditEmpty =
    !dimensionLength && !isSaasSubscription && productView
  const productViewdisableButton = detailView || isAdditionalDimensionEditEmpty
  return (
    <BoxV2
      heading={'Usage dimensions'}
      headingIcon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '18px', height: '14px' }}
        />
      }
      isOptional={!isSaasSubscription}
    >
      <div className={classes.textContainer} data-testid="text-container">
        <Typography className={classes.sectionText}>
          In a SaaS contract listing, you can choose to meter and charge your
          users on their additional usage by adding usage dimensions below.
          Assign a unique buyer-friendly description and price so your buyer
          knows what additional charges they may be charged upon apart from the
          usage permitted in the purchased contract dimension.
        </Typography>
      </div>
      <NoteV2
        description={
          'Your listing will only reflect the prices added by you once it is Public. All dimension prices before that would be set to $0.01 for testing purposes.'
        }
      />
      {dimensionLength < 1 ? (
        <Typography
          className={classes.noUsageDimensionContainer}
          data-testid="no-usage-dimension-container"
        >
          You haven’t defined any usage dimensions.
        </Typography>
      ) : (
        <></>
      )}
      <FieldArray name="productDimensions.additionalDimensions">
        {({ push, remove }) => (
          <>
            {additionalUsageDimentions.map((dimension, index) => (
              <div className={classes.usageRow} data-testid="usage-row">
                <div className={classes.titleAndIcon}>
                  <div className={classes.title} style={{ fontWeight: 500 }}>
                    Usage dimension {index + 1}
                  </div>
                  {((isSaasSubscription && dimensionLength > 1) ||
                    !isSaasSubscription) &&
                  !dimension?.isExisting ? (
                    <div
                      className={clsx(
                        classes.icon,
                        detailView ? classes.disableTrash : ''
                      )}
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        style={{ width: '18px', height: '20px' }}
                        data-testid="trash-additionalDimensions"
                        onClick={async () => {
                          await remove(index)
                          await changeIsAdditionalUsageAllowedOnContractDimension(
                            dimension.name
                          )
                        }}
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <div style={{ width: '100%' }}>
                  <Grid container spacing={4}>
                    <Grid size={5}>
                      <div
                        key={`contract-usage-${randomKey}-${getInputKey(
                          dimension.isAdditionalUsageAllowed || false,
                          'price'
                        )}`}
                      >
                        <LabraToolTipV2
                          followCursor={detailView}
                          tooltipPlacement="bottom-start"
                          description={detailViewTooltip}
                        >
                          <TextInputV2
                            keyName={`productDimensions.additionalDimensions.${index}.${getInputKey(
                              dimension.isAdditionalUsageAllowed || false,
                              'price'
                            )}`}
                            title={
                              dimensionCategoryType?.includes('HOURS')
                                ? `Enter price per ${dimensionCategoryType
                                    ?.split('-')[0]
                                    .toLowerCase()} per hour`
                                : 'Enter price'
                            }
                            type="number"
                            placeHolder="Enter price"
                            value={giveNumberInputValue(
                              dimension?.isAdditionalUsageAllowed
                                ? dimension.additionalUsagePrice
                                : dimension.subscriptionPrice
                            )}
                            updatedValue={giveNumberInputValue(
                              dimension?.isAdditionalUsageAllowed
                                ? dimension.additionalUsagePrice
                                : dimension.subscriptionPrice
                            )}
                            isError={getIsError(
                              `additionalDimensions.${[index]}.${getInputKey(
                                dimension.isAdditionalUsageAllowed || false,
                                'price'
                              )}`
                            )}
                            onChange={e =>
                              handleChangePrice(e, index, dimension)
                            }
                            isReadOnly={isFormLocked as boolean}
                            isRequired={false}
                            errorText={
                              getErrorText(
                                `additionalDimensions.${[index]}.${getInputKey(
                                  dimension.isAdditionalUsageAllowed || false,
                                  'price'
                                )}`
                              ) || ''
                            }
                            showSpinner={false}
                            onBlur={handleBlur}
                            keyToPrevent={['+', '-', 'e']}
                            viewOnly={detailView}
                          />
                        </LabraToolTipV2>
                      </div>
                    </Grid>
                    <Grid size={5}>
                      <div
                        key={`contract-usage-${randomKey}-${getInputKey(
                          dimension.isAdditionalUsageAllowed || false,
                          'label'
                        )}`}
                      >
                        <LabraToolTipV2
                          followCursor={detailView}
                          tooltipPlacement="bottom-start"
                          description={detailViewTooltip}
                        >
                          <TextInputV2
                            keyName={`productDimensions.additionalDimensions.${index}.${getInputKey(
                              dimension.isAdditionalUsageAllowed || false,
                              'label'
                            )}`}
                            title={'Enter display name'}
                            type="text"
                            placeHolder="Enter display name"
                            value={dimension?.label}
                            updatedValue={dimension?.label}
                            isError={getIsError(
                              `additionalDimensions.${[index]}.${getInputKey(
                                dimension.isAdditionalUsageAllowed || false,
                                'label'
                              )}`
                            )}
                            onChange={e =>
                              handleChangeLabel(e, index, dimension)
                            }
                            isReadOnly={isFormLocked as boolean}
                            isRequired={false}
                            maxCharacters={80}
                            errorText={
                              getErrorText(
                                `additionalDimensions.${[index]}.${getInputKey(
                                  dimension.isAdditionalUsageAllowed || false,
                                  'label'
                                )}`
                              ) || ''
                            }
                            showSpinner={false}
                            onBlur={handleBlur}
                            keyToPrevent={['+', '-', 'e']}
                            viewOnly={detailView}
                            highlights={
                              listingValidation?.value &&
                              getIsError(
                                `additionalDimensions.${[index]}.${getInputKey(
                                  dimension.isAdditionalUsageAllowed || false,
                                  'label'
                                )}`
                              )
                                ? getListingTextHighlights(
                                    dimension?.label,
                                    'plain'
                                  )
                                : []
                            }
                          />
                        </LabraToolTipV2>
                      </div>
                    </Grid>
                    {isSaasSubscription && (
                      <Grid size={12}>
                        <div className={classes.sectionText}>
                          This is the description of your usage dimension which
                          helps your buyers understand what usage they may be
                          charged upon
                        </div>
                      </Grid>
                    )}
                    <Grid size={10}>
                      <div
                        key={`contract-usage-${randomKey}-${getInputKey(
                          dimension.isAdditionalUsageAllowed || false,
                          'descrip'
                        )}`}
                      >
                        <LabraToolTipV2
                          followCursor={detailView}
                          tooltipPlacement="bottom-start"
                          description={detailViewTooltip}
                        >
                          <TextInputV2
                            keyName={`productDimensions.additionalDimensions.${index}.${getInputKey(
                              dimension.isAdditionalUsageAllowed || false,
                              'descrip'
                            )}`}
                            title="Enter a brief description"
                            type="text"
                            placeHolder="Enter a brief description"
                            maxCharacters={90}
                            value={
                              (dimension.isAdditionalUsageAllowed
                                ? dimension.additionalUsageDescription
                                : dimension.labelDescription) || ''
                            }
                            updatedValue={
                              (dimension.isAdditionalUsageAllowed
                                ? dimension.additionalUsageDescription
                                : dimension.labelDescription) || ''
                            }
                            isError={getIsError(
                              `additionalDimensions.${[index]}.${getInputKey(
                                dimension.isAdditionalUsageAllowed || false,
                                'descrip'
                              )}`
                            )}
                            onChange={e =>
                              handleDescriptionChange(e, index, dimension)
                            }
                            isReadOnly={isFormLocked as boolean}
                            isRequired={false}
                            errorText={getErrorText(
                              `additionalDimensions.${[index]}.${getInputKey(
                                dimension.isAdditionalUsageAllowed || false,
                                'descrip'
                              )}`
                            )}
                            showSpinner={false}
                            onBlur={handleBlur}
                            viewOnly={detailView}
                            highlights={
                              listingValidation?.value &&
                              getIsError(
                                `additionalDimensions.${[index]}.${getInputKey(
                                  dimension.isAdditionalUsageAllowed || false,
                                  'descrip'
                                )}`
                              )
                                ? getListingTextHighlights(
                                    (dimension.isAdditionalUsageAllowed
                                      ? dimension.additionalUsageDescription
                                      : dimension.labelDescription) || '',
                                    'plain'
                                  )
                                : []
                            }
                          />
                        </LabraToolTipV2>
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </div>
            ))}
            {dimensionLength <= 23 ? (
              <>
                <LabraToolTipV2
                  followCursor={productViewdisableButton}
                  tooltipPlacement="bottom-start"
                  description={detailViewTooltip}
                  className={
                    productViewdisableButton ? classes.buttonWrapper : ''
                  }
                >
                  <ButtonV2
                    styleType="link"
                    onClick={() =>
                      push({
                        ...emptyDimensionObj,
                      })
                    }
                    data-testid="add-a-usageDimension-button"
                    disabled={isFormLocked || productViewdisableButton}
                  >
                    Add a usage dimension
                  </ButtonV2>
                  <Typography className={classes.buttonSubText}>
                    You can add {24 - dimensionLength} more usage dimensions.
                  </Typography>
                </LabraToolTipV2>
              </>
            ) : (
              <></>
            )}
          </>
        )}
      </FieldArray>
    </BoxV2>
  )
}
